import { useState, useEffect } from 'react';
import { useQuery, useAction } from 'wasp/client/operations';
import {
  getLocTypeServiceTypes,
  getServiceTypes,
  linkServiceTypeToLocationType,
  unlinkServiceTypeFromLocationType,
  updateLocTypeServiceType
} from 'wasp/client/operations';
import { ChevronDown, ChevronRight, Plus, Trash2, Layers, Save, RotateCcw } from 'lucide-react';
import { toast } from '../../hooks/use-toast';

interface Props {
  locationTypeId: string;
}

export default function LocTypeServiceTypesTab({ locationTypeId }: Props) {
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [pendingSelections, setPendingSelections] = useState<Map<string, string[]>>(new Map());
  const [savingIds, setSavingIds] = useState<Set<string>>(new Set());

  const { data: linked = [], isLoading } = useQuery(getLocTypeServiceTypes, { locationTypeId });
  const { data: allServiceTypes = [] } = useQuery(getServiceTypes);

  const linkFn = useAction(linkServiceTypeToLocationType);
  const unlinkFn = useAction(unlinkServiceTypeFromLocationType);
  const updateFn = useAction(updateLocTypeServiceType);

  useEffect(() => {
    setPendingSelections(prev => {
      const next = new Map(prev);
      for (const item of linked as any[]) {
        if (!next.has(item.id)) {
          next.set(item.id, [...item.selectedServiceDefinitionIds]);
        }
      }
      const linkedIds = new Set((linked as any[]).map((l: any) => l.id));
      for (const key of next.keys()) {
        if (!linkedIds.has(key)) next.delete(key);
      }
      return next;
    });
  }, [linked]);

  const linkedServiceTypeIds = new Set((linked as any[]).map((l: any) => l.serviceTypeId));
  const available = (allServiceTypes as any[]).filter((st: any) => !linkedServiceTypeIds.has(st.id) && st.isActive);

  const toggleExpand = (id: string) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const handleAdd = async (serviceTypeId: string) => {
    try {
      const result = await linkFn({ locationTypeId, serviceTypeId }) as any;
      toast({ title: 'Service category added', description: 'All service names pre-selected.' });
      setExpandedIds(prev => new Set([...prev, result.id]));
    } catch (err: any) {
      toast({ title: 'Error', description: err.message || 'Failed to add.', variant: 'destructive' });
    }
  };

  const handleRemove = async (id: string, label: string) => {
    if (!confirm(`Remove service category "${label}"? All selections will be lost.`)) return;
    try {
      await unlinkFn({ id });
      setPendingSelections(prev => { const next = new Map(prev); next.delete(id); return next; });
      toast({ title: 'Removed', description: `"${label}" unlinked.` });
    } catch (err: any) {
      toast({ title: 'Error', description: err.message || 'Failed to remove.', variant: 'destructive' });
    }
  };

  const handleToggle = (itemId: string, defId: string) => {
    setPendingSelections(prev => {
      const next = new Map(prev);
      const current = next.get(itemId) ?? [];
      next.set(itemId, current.includes(defId) ? current.filter(id => id !== defId) : [...current, defId]);
      return next;
    });
  };

  const handleSelectAll = (itemId: string, allIds: string[], selectAll: boolean) => {
    setPendingSelections(prev => {
      const next = new Map(prev);
      next.set(itemId, selectAll ? [...allIds] : []);
      return next;
    });
  };

  const handleSave = async (item: any) => {
    const selected = pendingSelections.get(item.id) ?? [];
    setSavingIds(prev => new Set([...prev, item.id]));
    try {
      await updateFn({ id: item.id, selectedServiceDefinitionIds: selected });
      toast({ title: 'Saved', description: `Selection updated for "${item.serviceType.label}".` });
    } catch (err: any) {
      toast({ title: 'Error', description: err.message || 'Failed to save.', variant: 'destructive' });
    } finally {
      setSavingIds(prev => { const next = new Set(prev); next.delete(item.id); return next; });
    }
  };

  const handleReset = (item: any) => {
    setPendingSelections(prev => {
      const next = new Map(prev);
      next.set(item.id, [...item.selectedServiceDefinitionIds]);
      return next;
    });
  };

  const isDirty = (item: any): boolean => {
    const pending = pendingSelections.get(item.id);
    if (!pending) return false;
    const saved: string[] = item.selectedServiceDefinitionIds;
    if (pending.length !== saved.length) return true;
    const savedSet = new Set(saved);
    return pending.some((id: string) => !savedSet.has(id));
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Add */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Plus className="w-5 h-5" />
          Add Service Category
        </h3>
        {available.length === 0 ? (
          <p className="text-sm text-gray-500">All available service categories are already linked.</p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {available.map((st: any) => (
              <button
                key={st.id}
                onClick={() => handleAdd(st.id)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-white border border-gray-300 rounded-md hover:bg-blue-50 hover:border-blue-300 hover:text-blue-700 transition-colors"
              >
                <Plus className="w-3.5 h-3.5" />
                {st.label}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Linked */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Layers className="w-5 h-5" />
          Linked Service Categories
          <span className="ml-2 text-sm font-normal text-gray-500">({(linked as any[]).length})</span>
        </h3>

        {(linked as any[]).length === 0 ? (
          <div className="text-center py-8">
            <Layers className="w-10 h-10 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500 text-sm">No service categories linked yet.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {(linked as any[]).map((item: any) => {
              const isExpanded = expandedIds.has(item.id);
              const definitions = item.serviceType.serviceDefinitions;
              const allDefIds = definitions.map((sd: any) => sd.id);
              const selectedIds = pendingSelections.get(item.id) ?? item.selectedServiceDefinitionIds;
              const allSelected = definitions.length > 0 && selectedIds.length === definitions.length;
              const noneSelected = selectedIds.length === 0;
              const dirty = isDirty(item);
              const saving = savingIds.has(item.id);

              return (
                <div key={item.id} className="border border-gray-200 rounded-lg overflow-hidden">
                  <div className="flex items-center justify-between px-4 py-3 bg-white hover:bg-gray-50 transition-colors">
                    <button onClick={() => toggleExpand(item.id)} className="flex items-center gap-2 flex-1 text-left">
                      {isExpanded ? <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" /> : <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />}
                      <span className="font-medium text-gray-900">{item.serviceType.label}</span>
                      <span className="text-xs text-gray-400 ml-1">{selectedIds.length} / {definitions.length} selected</span>
                      {dirty && <span className="text-xs text-amber-600 font-medium ml-1">• unsaved</span>}
                    </button>
                    <button onClick={() => handleRemove(item.id, item.serviceType.label)} className="ml-3 inline-flex items-center gap-1 px-2 py-1 text-xs text-red-600 hover:bg-red-50 rounded transition-colors">
                      <Trash2 className="w-3.5 h-3.5" />
                      Remove
                    </button>
                  </div>

                  {isExpanded && (
                    <div className="border-t border-gray-100 bg-gray-50 px-4 py-3">
                      {definitions.length === 0 ? (
                        <p className="text-sm text-gray-400 italic">No active service names in this category.</p>
                      ) : (
                        <>
                          <div className="flex items-center justify-between mb-3 pb-2 border-b border-gray-200">
                            <div className="flex items-center gap-4">
                              <button onClick={() => handleSelectAll(item.id, allDefIds, true)} disabled={allSelected} className="text-xs text-blue-600 hover:text-blue-800 disabled:text-gray-400 disabled:cursor-not-allowed">Select all</button>
                              <button onClick={() => handleSelectAll(item.id, allDefIds, false)} disabled={noneSelected} className="text-xs text-blue-600 hover:text-blue-800 disabled:text-gray-400 disabled:cursor-not-allowed">Deselect all</button>
                            </div>
                            {dirty && (
                              <div className="flex items-center gap-2">
                                <button onClick={() => handleReset(item)} disabled={saving} className="inline-flex items-center gap-1 px-2 py-1 text-xs text-gray-600 hover:bg-gray-200 rounded transition-colors disabled:opacity-50">
                                  <RotateCcw className="w-3 h-3" />Reset
                                </button>
                                <button onClick={() => handleSave(item)} disabled={saving} className="inline-flex items-center gap-1 px-3 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors disabled:opacity-50">
                                  <Save className="w-3 h-3" />{saving ? 'Saving...' : 'Save'}
                                </button>
                              </div>
                            )}
                          </div>
                          <div className="space-y-2">
                            {definitions.map((sd: any) => (
                              <label key={sd.id} className="flex items-center gap-3 cursor-pointer group">
                                <input type="checkbox" checked={selectedIds.includes(sd.id)} onChange={() => handleToggle(item.id, sd.id)} className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500" />
                                <span className="text-sm text-gray-800 group-hover:text-gray-900">{sd.name}</span>
                              </label>
                            ))}
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}



